/**
 * @file LegalDocBody.tsx
 * @description Содержимое карточки документа: общие поля и секция вида
 * (phase19.16, SB139–SB143; uiux-legal-docs §3–§4).
 *
 * Секция вида — реестром `KIND_SECTIONS` (текст — редактор и редакции, PDF —
 * файл и показ, группа — состав), а не ветками: новый вид = запись. Новый
 * документ (без карточки) и открытая карточка рисуются одним компонентом.
 */

import type { LegalDocumentDto, LegalDocumentKind } from "@chaiberi/shared";
import type { ReactNode } from "react";

import { SectionCard } from "../ui/SectionCard";
import { LegalDocFields } from "./LegalDocFields";
import { LegalGroupCard } from "./LegalGroupCard";
import { LegalPdfCard } from "./LegalPdfCard";
import { LegalRevisionHistory } from "./LegalRevisionHistory";
import { LegalTextEditor } from "./LegalTextEditor";
import type { LegalFileUpload } from "../../hooks/legal/useLegalFileUpload";
import type { LegalDraftErrors, LegalDraftValues } from "../../hooks/legal/useLegalDraftForm";
import type { LegalRevisionsState } from "../../hooks/legal/useLegalRevisions";
import { cn } from "@/lib/utils";

export interface LegalDocBodyProps {
  kind: LegalDocumentKind;
  /** Карточка; null — новый документ. */
  doc: LegalDocumentDto | null;
  values: LegalDraftValues;
  onChange: (patch: Partial<LegalDraftValues>) => void;
  errors: LegalDraftErrors;
  upload: LegalFileUpload;
  onPickFile: (file: File) => void;
  onRevertFile?: () => void;
  pendingFile?: File | null;
  revisions?: LegalRevisionsState;
  /** Карточка из ответа API (состав группы). */
  onDoc: (doc: LegalDocumentDto) => void;
  fail: (err: unknown, fallback: string) => void;
  disabled?: boolean;
  /** Над полями: шаблон нового текста. */
  before?: ReactNode;
}

/** Секции вида: заголовок и содержимое. */
const KIND_SECTIONS: Readonly<Record<LegalDocumentKind, (p: LegalDocBodyProps) => ReactNode>> = {
  TEXT: (p) => (
    <>
      <SectionCard title="Текст">
        <LegalTextEditor title={p.values.title} value={p.values.contentHtml} onChange={(contentHtml) => p.onChange({ contentHtml })} hasLive={!!p.doc?.live} disabled={p.disabled} />
      </SectionCard>
      {p.doc && p.revisions ? (
        <SectionCard title="Редакции">
          <LegalRevisionHistory docId={p.doc.id} kind="TEXT" revisions={p.revisions} liveNumber={p.doc.live?.number ?? null} />
        </SectionCard>
      ) : null}
    </>
  ),
  PDF: (p) => (
    <SectionCard title="Файл и показ на сайте">
      <LegalPdfCard
        doc={p.doc}
        values={p.values}
        onChange={p.onChange}
        upload={p.upload}
        onPickFile={p.onPickFile}
        onRevertFile={p.onRevertFile}
        pendingFile={p.pendingFile}
        revisions={p.revisions}
        disabled={p.disabled}
      />
    </SectionCard>
  ),
  GROUP: (p) => (
    <SectionCard title="Состав группы">
      {p.doc ? (
        <LegalGroupCard doc={p.doc} onDoc={p.onDoc} fail={p.fail} />
      ) : (
        <p className="type-body text-on-surface-variant">Документы добавляются после создания группы — нажмите «Сохранить».</p>
      )}
    </SectionCard>
  ),
};

export function LegalDocBody(props: LegalDocBodyProps) {
  const { kind, doc, values, onChange, errors, disabled, before } = props;
  return (
    <div className={cn("grid w-full grid-cols-1 gap-4", kind === "TEXT" ? "max-w-[960px]" : "xl:grid-cols-2 xl:items-start")}>
      <SectionCard title={kind === "GROUP" ? "Группа" : "Документ"}>
        {before}
        <LegalDocFields kind={kind} values={values} onChange={onChange} errors={errors} isNew={doc === null} disabled={disabled} />
      </SectionCard>
      <div className="flex min-w-0 flex-col gap-4">{KIND_SECTIONS[kind](props)}</div>
    </div>
  );
}
